import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Router from 'routers';
import ErrorBoundary from './components/errors/ErrorBoundary';
import Loading from './components/loading';
import { setUser, resetLogin, setIsProfileSubmitted, resetInvitationState } from 'redux/reducers/login';
import { auth } from 'services/firebase/config';
import { handleError } from 'utils/errorHandler';
import queryRef from 'services/firebase/query';
import { getDocData } from 'utils/format';
import * as localStorage from 'utils/storage';
import { USER_STATUS } from 'constants/options';
import firebaseAuthService from 'services/firebase/auth';
import { UserIsSuspendedError } from 'utils/errors/firebaseError';
import { searchClient, algoliaIndexName } from './services/algolia';
import { InstantSearch } from 'react-instantsearch-dom';
import { useTitle } from 'hooks';
import { appConfig } from 'config/appConfig';

const App = props => {
  const { setUser, resetLogin, setIsProfileSubmitted, isInvitationLoading, resetInvitationState } = props;
  const [authState, setAuthState] = useState({
    isAuthenticate: false,
    initializing: true,
    isSuperAdmin: false,
  });
  const [algoliaState, setAlgoliaState] = useState({});
  // const algoliaState = useSelector(state => state.algolia);

  const [algoliaConfig, setAlgoliaConfig] = useState({});
  const userUnSubscribeRef = useRef(null);

  // custom title for web portal
  useTitle(appConfig.name);

  const AlgoliaConfig = async () => {
    let search = await searchClient();
    setAlgoliaConfig(search);
  };

  const logoutHandler = () => {
    firebaseAuthService
      .logout()
      .catch(error => {
        handleError(error);
      })
      .finally(() => {
        setAuthState({
          isAuthenticate: false,
          initializing: false,
        });
      });
  };
  const algoUserSearch = localStorage.get('algoUserSearch');
  useEffect(() => {
    const algoMenu = localStorage.get('algoMenu');
    const objectifiedAlgoMenu = JSON.parse(algoMenu);
    const algoPage = localStorage.get('algoPage') ? Number(localStorage.get('algoPage')) : 1;
    const algoSort = localStorage.get('algoSort');
    const algoUserMenu = localStorage.get('algoUserMenu');
    const algoUserPage = localStorage.get('algoUserPage');
    const algoUserSort = localStorage.get('algoUserSort');

    const algoUser = {
      ...(algoUserMenu && { menu: { status: algoUserMenu } }),
      ...(algoUserPage && { page: algoUserPage }),
      ...(algoUserSort && { sortBy: algoUserSort }),
    };

    const algoAppUserPage = localStorage.get('algoAppUserPage');
    const algoAppUserSort = localStorage.get('algoAppUserSort');

    const algoAppUser = {
      ...(algoAppUserPage && { page: algoAppUserPage }),
      ...(algoAppUserSort && { sortBy: algoAppUserSort }),
    };

    const algoState = {
      menu: objectifiedAlgoMenu,
      page: algoPage,
      ...(algoSort && { sortBy: algoSort }),
      indices: { users: algoUser, appUsers: algoAppUser },
      query: localStorage.get('algoUserSearch'),
    };
    setAlgoliaState(algoState);
  }, [algoUserSearch]);

  useEffect(() => {
    AlgoliaConfig();
  }, [algoliaState]);

  const algoStateChangeHandler = searchState => {
    const algoMenu = localStorage.get('algoMenu');
    const parsedAlgoMenu = JSON.parse(algoMenu);
    const stringfyAlgoState = JSON.stringify({ ...parsedAlgoMenu, ...searchState.menu });
    localStorage.set('algoMenu', stringfyAlgoState);
    if (searchState.page) {
      localStorage.set('algoPage', searchState.page);
    }

    if (searchState.query) {
      localStorage.set('algoUserSearch', searchState.query);
    }
    if (!searchState.query && localStorage.get('algoUserSearch')?.length <= 1) {
      localStorage.set('algoUserSearch', '');
    }

    if (searchState.sortBy) {
      localStorage.set('algoSort', searchState.sortBy);
    }
    if (searchState?.indices?.users?.menu?.status) {
      localStorage.set('algoUserMenu', searchState.indices.users.menu.status);
    }
    if (searchState?.indices?.users?.page) {
      localStorage.set('algoUserPage', searchState.indices.users.page);
    }
    if (searchState?.indices?.users?.sortBy) {
      localStorage.set('algoUserSort', searchState.indices.users.sortBy);
    }
    if (searchState?.indices?.appUsers?.page) {
      localStorage.set('algoAppUserPage', searchState.indices.appUsers.page);
    }
    if (searchState?.indices?.appUsers?.sortBy) {
      localStorage.set('algoAppUserSort', searchState.indices.appUsers.sortBy);
    }
    const algoPage = localStorage.get('algoPage') ? Number(localStorage.get('algoPage')) : 1;
    const algoSort = localStorage.get('algoSort');
    const algoUserMenu = localStorage.get('algoUserMenu');
    const algoUserPage = localStorage.get('algoUserPage');
    const algoUserSort = localStorage.get('algoUserSort');
    const algoUserSearch = localStorage.get('algoUserSearch');

    const algoUser = {
      ...(algoUserMenu && { menu: { status: algoUserMenu } }),
      ...(algoUserPage && { page: algoUserPage }),
      ...(algoUserSort && { sortBy: algoUserSort }),
      ...(searchState?.indices?.users?.query && { query: searchState.indices.users.query }),
    };

    const algoAppUserPage = localStorage.get('algoAppUserPage') ? Number(localStorage.get('algoAppUserPage')) : 1;
    const algoAppUserSort = localStorage.get('algoAppUserSort');

    const algoAppUser = {
      ...(algoAppUserPage && { page: algoAppUserPage }),
      ...(algoAppUserSort && { sortBy: algoAppUserSort }),
      ...(searchState?.indices?.appUsers?.query && { query: searchState.indices.appUsers.query }),
    };

    setAlgoliaState({
      ...searchState,
      ...(algoSort && { sortBy: algoSort }),
      page: algoPage,
      menu: { ...parsedAlgoMenu, ...searchState.menu },
      indices: { users: algoUser, appUsers: algoAppUser },
      query: algoUserSearch,
    });
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) {
        userUnSubscribeRef.current && userUnSubscribeRef.current();
        setAuthState({
          isAuthenticate: false,
          initializing: false,
        });
        resetLogin();
        !isInvitationLoading && resetInvitationState();
        return;
      }
      /* For client invitaion flow, discard the user snapshot listener until the invitation flow is completed
       Other wise it will show `unauthorized` error during invitation process */
      if (isInvitationLoading) {
        return;
      }
      userUnSubscribeRef.current = queryRef.USER_INFO_BY_ID(user.uid).onSnapshot(
        response => {
          const userInfo = getDocData(response);

          if (userInfo && (userInfo.isSuperAdmin || userInfo.status !== USER_STATUS.suspended)) {
            setUser(userInfo);
            if (!userInfo.isProfileCompleted) {
              setIsProfileSubmitted(userInfo.isProfileCompleted);
            }
            setAuthState({
              isAuthenticate: true,
              initializing: false,
            });
            localStorage.set('lastLoginRole', userInfo.isSuperAdmin ? 'admin' : 'client');
          } else {
            //For suspendedUser, revoke the social login session
            const suspendedError = new UserIsSuspendedError();
            handleError(suspendedError);
            logoutHandler();
          }
        },
        error => {
          //revoke the current sociallogin session if error on getting userInfo
          logoutHandler();
          handleError(error);
        }
      );
    });
    AlgoliaConfig();
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [setUser, resetLogin, setIsProfileSubmitted, isInvitationLoading, resetInvitationState]);

  return (
    <ErrorBoundary>
      {authState.initializing || isInvitationLoading ? (
        <Loading />
      ) : (
        <>
          <InstantSearch
            searchClient={algoliaConfig}
            indexName={algoliaIndexName}
            refresh={true}
            searchState={algoliaState}
            // onSearchStateChange={searchState => dispatch(setSearchStateChange(searchState))}
            onSearchStateChange={algoStateChangeHandler}
          >
            <Router isAuthenticate={authState.isAuthenticate} />
          </InstantSearch>
        </>
      )}
    </ErrorBoundary>
  );
};

App.propTypes = {
  setUser: PropTypes.func.isRequired,
  resetLogin: PropTypes.func.isRequired,
  setIsProfileSubmitted: PropTypes.func.isRequired,
  isInvitationLoading: PropTypes.bool,
  resetInvitationState: PropTypes.func.isRequired,
};

App.defaultProps = {
  isInvitationLoading: false,
};

const mapStateToProps = state => ({
  isInvitationLoading: state.login.isInvitationLoading,
});

const mapDispatchToProps = dispatch => ({
  setUser: user => dispatch(setUser(user)),
  resetLogin: () => dispatch(resetLogin()),
  setIsProfileSubmitted: status => dispatch(setIsProfileSubmitted(status)),
  resetInvitationState: () => dispatch(resetInvitationState()),
});
export default connect(mapStateToProps, mapDispatchToProps)(App);
